import React, { useState, useEffect } from "react";
import Button from "../../Button/Button";
import CheckIcon from "../../../assets/svg/check-icon";
import { useDispatch, useSelector } from "react-redux";
import {
  setCommunicationsLease,
  selectCommunicationsFilters,
} from "../../../redux/slices/filterSlice";
import { LeaseDuration, LeaseRange } from "../types";
import { RootState } from "../../../redux/store";

interface QuickFilter {
  label: string;
  min_lease: number;
  max_lease: number;
  unit: LeaseDuration;
  description: string;
}

type LeaseType = "week" | "month";

const LeaseFilter = ({ onClose }: { onClose: () => void }) => {
  const dispatch = useDispatch();
  const communicationsFilters = useSelector(selectCommunicationsFilters);

  const [selectedQuickFilters, setSelectedQuickFilters] = useState<
    QuickFilter[]
  >([]);
  const [selectedLeaseType, setSelectedLeaseType] = useState<LeaseType>("week");
  const fontSize = useSelector((state: RootState) => state.fontSettings.fontSize);

  const weekFilters: QuickFilter[] = [
    {
      label: "8 - 12 weeks",
      description: "Short Term / Summer",
      min_lease: 8,
      max_lease: 12,
      unit: LeaseDuration.WEEKS,
    },
    {
      label: "13 - 24  weeks",
      description: "1 Semester",
      min_lease: 13,
      max_lease: 24,
      unit: LeaseDuration.WEEKS,
    },
    {
      label: "25 - 36 weeks",
      description: "2 Semesters",
      min_lease: 25,
      max_lease: 36,
      unit: LeaseDuration.WEEKS,
    },
    {
      label: "37 - 44 weeks",
      description: "Short Full Year",
      min_lease: 37,
      max_lease: 44,
      unit: LeaseDuration.WEEKS,
    },
    {
      label: "45 - 52 weeks",
      description: "Full Year",
      min_lease: 45,
      max_lease: 52,
      unit: LeaseDuration.WEEKS,
    },
    {
      label: "52 - 54 weeks",
      description: "Extended Full-Year/Renewal",
      min_lease: 52,
      max_lease: 54,
      unit: LeaseDuration.WEEKS,
    },
  ];
  // Quick filters for months
  const monthFilters: QuickFilter[] = [
    {
      label: "0 - 3 months",
      description: "Short Term",
      min_lease: 0,
      max_lease: 3,
      unit: LeaseDuration.MONTHS,
    },
    {
      label: "4 - 6 months",
      description: "Medium Term",
      min_lease: 4,
      max_lease: 6,
      unit: LeaseDuration.MONTHS,
    },
    {
      label: "7 - 9 months",
      description: "Long Term",
      min_lease: 7,
      max_lease: 9,
      unit: LeaseDuration.MONTHS,
    },
    {
      label: "10 - 12 months",
      description: "Full Year",
      min_lease: 10,
      max_lease: 12,
      unit: LeaseDuration.MONTHS,
    },
  ];

  const currentFilters =
    selectedLeaseType === "week" ? weekFilters : monthFilters;

  // Initialize state from Redux on mount
  useEffect(() => {
    if (
      communicationsFilters.lease.value &&
      communicationsFilters.lease.value.length > 0
    ) {
      const matchedFilters: QuickFilter[] = [];

      communicationsFilters.lease.value.forEach((range: LeaseRange) => {
        // Determine which type based on the unit
        const isWeeks = range.unit === LeaseDuration.WEEKS;
        const filters = isWeeks ? weekFilters : monthFilters;

        // Find matching filter (assuming min and max are the same for quick filters)
        const matchingFilter = filters.find(
          (qf) =>
            qf.min_lease === range.min_lease &&
            qf.max_lease === range.max_lease &&
            qf.unit === range.unit
        );

        if (matchingFilter) {
          matchedFilters.push(matchingFilter);
          if (isWeeks) {
            setSelectedLeaseType("week");
          } else {
            setSelectedLeaseType("month");
          }
        }
      });

      if (matchedFilters.length > 0) {
        setSelectedQuickFilters(matchedFilters);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isQuickFilterSelected = (filter: QuickFilter) => {
    return selectedQuickFilters.some(
      (qf) =>
        qf.min_lease === filter.min_lease &&
        qf.max_lease === filter.max_lease &&
        qf.unit === filter.unit
    );
  };

  const handleQuickFilterClick = (filter: QuickFilter) => {
    if (isQuickFilterSelected(filter)) {
      // Remove filter
      setSelectedQuickFilters((prev) =>
        prev.filter(
          (qf) =>
            !(
              qf.min_lease === filter.min_lease &&
              qf.max_lease === filter.max_lease &&
              qf.unit === filter.unit
            )
        )
      );
    } else {
      // Add filter
      setSelectedQuickFilters((prev) => [...prev, filter]);
    }
  };

  const handleLeaseTypeChange = (type: LeaseType) => {
    setSelectedLeaseType(type);
    // Clear selections when switching between week/month
    setSelectedQuickFilters([]);
  };

  const handleApply = () => {
    if (selectedQuickFilters.length > 0) {
      // Convert selected filters to LeaseRange format
      const leaseRanges: LeaseRange[] = selectedQuickFilters.map((qf) => ({
        min_lease: qf.min_lease,
        max_lease: qf.max_lease,
        unit: qf.unit,
      }));

      const displayValue = selectedQuickFilters.map((qf) => qf.label);

      dispatch(
        setCommunicationsLease({
          value: leaseRanges,
          displayValue: displayValue,
        })
      );
    }
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  const isApplyDisabled = selectedQuickFilters.length === 0;

  // Calculate fill percentage based on position in the list
  const getFillPercentage = (index: number, total: number) => {
    return ((index + 1) / total) * 100;
  };

  return (
    <div className="flex flex-col gap-2 mt-2">
      {/* Week/Month Toggle */}
      <div className="flex flex-row items-center">
        <div className={`border border-border-gray dark:border-border-gray-dk rounded-lg p-2 flex gap-2 ${fontSize === 4 || fontSize === 3 ? "w-[140px]" : "w-[120px]"} mx-3`}>
          <Button
            innerText="Weeks"
            buttonContainerClass={`w-[50%] py-0.5 px-1.5 ${
              selectedLeaseType === "week"
                ? "bg-verba-blue dark:bg-verba-blue-dk border-verba-blue dark:border-verba-blue-dk rounded-sm"
                : ""
            }`}
            onClick={() => handleLeaseTypeChange("week")}
            innerTextClass={`text-sm font-medium whitespace-nowrap ${
              selectedLeaseType === "week"
                ? "text-fixed-white dark:text-fixed-white-dk"
                : "text-heading-text dark:text-heading-text-dk"
            }`}
          />
          <Button
            innerText="Months"
            buttonContainerClass={`w-[50%] py-0.5 px-1.5 ${
              selectedLeaseType === "month"
                ? "bg-verba-blue dark:bg-verba-blue-dk  border-verba-blue dark:border-verba-blue-dk rounded-sm"
                : ""
            }`}
            onClick={() => handleLeaseTypeChange("month")}
            innerTextClass={`text-sm font-medium whitespace-nowrap ${
              selectedLeaseType === "month"
                ? "text-fixed-white dark:text-fixed-white-dk"
                : "text-heading-text dark:text-heading-text-dk"
            }`}
          />
        </div>{" "}
      </div>

      {/* Quick Filters */}
      <div className="flex flex-row items-center justify-between px-3">
        <h2 className="text-md text-verba-field-label dark:text-verba-field-label-dk">
          Quick Filters
        </h2>
        <span className="text-xxs font-semibold text-data dark:text-data-dk rounded-sm">
          SELECTED ({selectedQuickFilters.length})
        </span>
      </div>
      {currentFilters.map((option, index) => {
        const fillPercentage = getFillPercentage(index, currentFilters.length);
        const circumference = 2 * Math.PI * 10; // radius = 10
        const strokeDashoffset =
          circumference - (fillPercentage / 100) * circumference;

        return (
          <div
            key={option.label}
            onClick={() => handleQuickFilterClick(option)}
            className={`flex items-center gap-3 p-3 mx-3 text-md cursor-pointer border border-border-gray dark:border-border-gray-dk rounded-lg text-heading-text dark:text-heading-text-dk ${
              isQuickFilterSelected(option)
                ? "bg-verba-blue-light-card-bg dark:bg-verba-blue-light-card-bg-dk border-verba-blue dark:border-verba-blue-dk"
                : ""
            }`}
          >
            {/* Circular Progress Border */}
            <div className="shrink-0 w-6 h-6 relative">
              <svg className="w-6 h-6 transform -rotate-90" viewBox="0 0 24 24">
                {/* Background circle */}
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="2"
                  fill="none"
                  className="text-gray-75 dark:text-gray-75-dk"
                />
                {/* Progress circle */}
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="2"
                  fill="none"
                  strokeDasharray={circumference}
                  strokeDashoffset={strokeDashoffset}
                  strokeLinecap="round"
                  className="text-verba-indigo dark:text-verba-indigo-dk transition-all duration-300"
                />
              </svg>
            </div>

            {/* Content */}
            <div className="flex-1 flex flex-col gap-1">
              <div className="text-md font-medium">{option.label}</div>
              {option.description && (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {option.description}
                </p>
              )}
            </div>

            {/* Checkbox */}
            <div
              className={`shrink-0 w-[14px] h-[14px] border border-gray-100 dark:border-gray-100-dk rounded-[3px] flex items-center justify-center ${
                isQuickFilterSelected(option)
                  ? "bg-verba-blue text-pure-white dark:bg-verba-blue-dk dark:text-pure-white-dk border-verba-blue dark:border-verba-blue-dk"
                  : ""
              }`}
            >
              {isQuickFilterSelected(option) && (
                <CheckIcon className="w-3 h-3 text-pure-white dark:text-pure-white-dk" />
              )}
            </div>
          </div>
        );
      })}

      {/* Action Buttons */}
      <div className="flex justify-end gap-2 mt-4 px-3">
        <Button
          innerText="Cancel"
          onClick={handleCancel}
          buttonContainerClass="py-1 px-7.5 border border-verba-indigo dark:border-verba-indigo-dk rounded-lg"
          innerTextClass="text-md font-medium whitespace-nowrap text-heading-text dark:text-heading-text-dk"
        />
        <Button
          innerText="Apply"
          onClick={handleApply}
          disabled={isApplyDisabled}
          buttonContainerClass="py-1 px-7.5 rounded-lg bg-verba-indigo dark:bg-verba-indigo-dk disabled:opacity-50 disabled:cursor-not-allowed"
          innerTextClass="text-md font-medium whitespace-nowrap text-pure-white dark:text-pure-white-dk"
        />
      </div>
    </div>
  );
};

export default LeaseFilter;
